import { useState } from 'react';
import { useTranslate, useUpdate, useGetIdentity, useNotify, useRefresh } from 'react-admin';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArchiveIcon from '@mui/icons-material/Archive';
import PendingIcon from '@mui/icons-material/Pending';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { TicketStatus, TicketPriority, type Ticket } from '../../../types/chat.types';
import { useTicketActions } from '../../../hooks/useTicketActions';
import { TransferTicketDialog } from '../../../components/ticket/TransferTicketDialog';

interface TicketContextMenuProps {
  ticket: Ticket | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  onActionComplete?: () => void;
}

const STATUS_OPTIONS = [
  { value: TicketStatus.OPEN, icon: <RadioButtonCheckedIcon fontSize="small" sx={{ color: 'warning.main' }} />, key: 'open' },
  { value: TicketStatus.RESOLVED, icon: <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />, key: 'resolved' },
  { value: TicketStatus.CLOSED, icon: <ArchiveIcon fontSize="small" sx={{ color: 'text.secondary' }} />, key: 'closed' },
  { value: TicketStatus.PENDING, icon: <PendingIcon fontSize="small" sx={{ color: 'info.main' }} />, key: 'pending' },
] as const;

const PRIORITY_OPTIONS = [
  { value: null, key: 'none', color: 'text.disabled' },
  { value: TicketPriority.LOW, key: 'low', color: 'success.main' },
  { value: TicketPriority.MEDIUM, key: 'medium', color: 'info.main' },
  { value: TicketPriority.HIGH, key: 'high', color: 'warning.main' },
  { value: TicketPriority.URGENT, key: 'urgent', color: 'error.main' },
] as const;

export function TicketContextMenu({
  ticket,
  anchorPosition,
  onClose,
  onActionComplete,
}: TicketContextMenuProps) {
  const translate = useTranslate();
  const [update] = useUpdate();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const refresh = useRefresh();
  const { close: closeTicket, reopen, returnToPending, transfer } = useTicketActions();

  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [priorityAnchor, setPriorityAnchor] = useState<HTMLElement | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);

  const isOpen = Boolean(anchorPosition) && Boolean(ticket);
  const currentUserId = identity?.id ? Number(identity.id) : 0;
  const isAssignedToMe = ticket?.assignedUserId === currentUserId;

  const handleClose = () => {
    setStatusAnchor(null);
    setPriorityAnchor(null);
    onClose();
  };

  const handleStatusChange = async (newStatus: TicketStatus) => {
    if (!ticket) return;
    handleClose();

    try {
      if (newStatus === TicketStatus.CLOSED) {
        await closeTicket(Number(ticket.id));
      } else if (newStatus === TicketStatus.PENDING) {
        await returnToPending(Number(ticket.id));
      } else if (ticket.status === TicketStatus.CLOSED && newStatus === TicketStatus.OPEN) {
        await reopen(Number(ticket.id));
      } else {
        await update('ticket', {
          id: ticket.id,
          data: { status: newStatus },
          previousData: ticket,
        });
        refresh();
      }
      onActionComplete?.();
    } catch {
      // Errors handled by useTicketActions / useUpdate
    }
  };

  const handlePriorityChange = async (newPriority: TicketPriority | null) => {
    if (!ticket) return;
    handleClose();

    try {
      await update('ticket', {
        id: ticket.id,
        data: { priority: newPriority },
        previousData: ticket,
      });
      refresh();
      onActionComplete?.();
    } catch {
      // Error handled by useUpdate
    }
  };

  const handleAssignToMe = async () => {
    if (!ticket) return;
    handleClose();

    try {
      await update('ticket', {
        id: ticket.id,
        data: { assignedUserId: currentUserId, status: TicketStatus.OPEN },
        previousData: ticket,
      });
      refresh();
      onActionComplete?.();
    } catch {
      // Error handled by useUpdate
    }
  };

  const handleUnassign = async () => {
    if (!ticket) return;
    handleClose();

    try {
      await update('ticket', {
        id: ticket.id,
        data: { assignedUserId: null },
        previousData: ticket,
      });
      refresh();
      onActionComplete?.();
    } catch {
      // Error handled by useUpdate
    }
  };

  const handleTransfer = async (data: { toUserId?: number; toQueueId?: number }) => {
    if (!ticket) return;
    await transfer(Number(ticket.id), data);
    setTransferOpen(false);
    onActionComplete?.();
  };

  const handleCopyLink = () => {
    if (!ticket) return;
    const url = `${window.location.origin}/tickets?ticket=${ticket.id}`;
    navigator.clipboard.writeText(url);
    notify('resources.tickets.contextMenu.linkCopied', { type: 'info' });
    handleClose();
  };

  const handleOpenNewTab = () => {
    if (!ticket) return;
    window.open(`${window.location.origin}/tickets?ticket=${ticket.id}`, '_blank');
    handleClose();
  };

  return (
    <>
      <Menu
        open={isOpen}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition ?? undefined}
        slotProps={{
          paper: {
            sx: { minWidth: 200, maxWidth: 280 },
          },
        }}
      >
        {/* Change Status submenu trigger */}
        <MenuItem
          onClick={(e) => setStatusAnchor(e.currentTarget)}
        >
          <ListItemIcon>
            <RadioButtonCheckedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('resources.tickets.contextMenu.changeStatus')}</ListItemText>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>&#9656;</Typography>
        </MenuItem>

        {/* Change Priority submenu trigger */}
        <MenuItem
          onClick={(e) => setPriorityAnchor(e.currentTarget)}
        >
          <ListItemIcon>
            <FlagIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('resources.tickets.contextMenu.changePriority')}</ListItemText>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>&#9656;</Typography>
        </MenuItem>

        <Divider />

        {/* Assign to me */}
        {!isAssignedToMe && (
          <MenuItem onClick={handleAssignToMe}>
            <ListItemIcon>
              <PersonAddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('resources.tickets.contextMenu.assignToMe')}</ListItemText>
          </MenuItem>
        )}

        {/* Unassign */}
        {ticket?.assignedUserId && (
          <MenuItem onClick={handleUnassign}>
            <ListItemIcon>
              <PersonRemoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('resources.tickets.contextMenu.unassign')}</ListItemText>
          </MenuItem>
        )}

        {/* Transfer */}
        <MenuItem onClick={() => { handleClose(); setTransferOpen(true); }}>
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('resources.tickets.contextMenu.transfer')}</ListItemText>
        </MenuItem>

        <Divider />

        {/* Copy link */}
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('resources.tickets.contextMenu.copyLink')}</ListItemText>
        </MenuItem>

        {/* Open in new tab */}
        <MenuItem onClick={handleOpenNewTab}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('resources.tickets.contextMenu.openNewTab')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Status submenu */}
      <Menu
        open={Boolean(statusAnchor)}
        anchorEl={statusAnchor}
        onClose={() => setStatusAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {STATUS_OPTIONS.filter(opt => opt.value !== ticket?.status).map((opt) => (
          <MenuItem key={opt.key} onClick={() => handleStatusChange(opt.value)}>
            <ListItemIcon>{opt.icon}</ListItemIcon>
            <ListItemText>{translate(`resources.ticket.status.${opt.key}`)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Priority submenu */}
      <Menu
        open={Boolean(priorityAnchor)}
        anchorEl={priorityAnchor}
        onClose={() => setPriorityAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {PRIORITY_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.key}
            onClick={() => handlePriorityChange(opt.value)}
            selected={ticket?.priority === opt.value || (!ticket?.priority && !opt.value)}
          >
            <ListItemIcon>
              {opt.value ? (
                <FlagIcon fontSize="small" sx={{ color: opt.color }} />
              ) : (
                <FlagOutlinedIcon fontSize="small" sx={{ color: opt.color }} />
              )}
            </ListItemIcon>
            <ListItemText>{translate(`resources.ticket.priority.${opt.key}`)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Transfer Dialog */}
      <TransferTicketDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onTransfer={handleTransfer}
        currentUserId={currentUserId}
      />
    </>
  );
}
